import React, { forwardRef } from 'react';
import AutoSizeTextArea from 'react-textarea-autosize';
import {
  DocumentNameInput,
  SingleDocument,
  Textarea,
  useBoolean,
  VStack,
} from '@wegroup/design-system';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { SmtFile } from '../../../../../../../types/Form';
import { InferType, object, string } from 'yup';
import PartyCreationFormInput from '../../../../../../parties/src/features/creation/components/PartyCreationFormInput';
import { useTranslation } from 'react-i18next';

const linkEditSchema = object({
  filename: string()
    .required('REQUIRED')
    .matches(/.+\.[a-zA-Z0-9]+$/, 'REQUIRED'),
  description: string(),
});

export type LinkEditValues = InferType<typeof linkEditSchema>;

interface Props {
  file?: File;
  document: SmtFile;
  onRemove: () => void;
  onSubmit: (values: LinkEditValues) => void;
  isEditable?: boolean;
  alwaysOpen?: boolean;
}

const LinkItem = forwardRef<HTMLInputElement, Props>(
  ({ file, document, onRemove, onSubmit, isEditable, alwaysOpen }, ref) => {
    const { t } = useTranslation();
    const defaultValues = {
      filename: document?.filename || file?.name,
      description: document?.description,
    };
    const [isEditing, setIsEditing] = useBoolean(alwaysOpen);
    const methods = useForm<LinkEditValues>({
      resolver: yupResolver(linkEditSchema),
      defaultValues,
    });

    const handleCancel = () => {
      methods.reset(defaultValues);
      setIsEditing.off();
    };

    const handleSave = methods.handleSubmit((values) => {
      /** Filter out type when it is an empty value */
      onSubmit(values);
      methods.reset(values);
      if (!alwaysOpen) setIsEditing.off();
    });

    return (
      <SingleDocument
        file={file}
        filename={document?.filename}
        description={document?.description}
        isEditing={isEditing}
        onEdit={isEditable ? setIsEditing.on : undefined}
        onSave={handleSave}
        onCancel={handleCancel}
        onRemove={onRemove}
        canDownload
      >
        <FormProvider {...methods}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
          >
            <VStack
              p="3"
              bg="gray.ultraLight"
              border="1px"
              borderColor="gray.50"
              borderRadius="md"
              spacing="3"
            >
              <PartyCreationFormInput
                label={t('NAME')}
                isRequired
                error={methods.formState?.errors?.filename}
              >
                <Controller
                  control={methods.control}
                  name="filename"
                  render={({ field }) => <DocumentNameInput {...field} />}
                />
              </PartyCreationFormInput>
              <PartyCreationFormInput
                label={t('DESCRIPTION')}
                error={methods.formState?.errors.description}
              >
                <Textarea
                  {...methods.register('description')}
                  as={AutoSizeTextArea}
                />
              </PartyCreationFormInput>
            </VStack>
            <input type="submit" style={{ display: 'none' }} ref={ref} />
          </form>
        </FormProvider>
      </SingleDocument>
    );
  },
);

export default LinkItem;
